
<template>
    <button
        class="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 bg-white transition-colors duration-300 hover:bg-blue-500 hover:text-white rounded-md text-sm"
        @click="redirectToLastPage()"
    >
        <svg class="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Retour
    </button>
    
    <div class="mb-5 mt-5">
        <h1 class="page-title">{{ title }}</h1>
    </div>

    <div class="card">
        <DataTable
            :value="tableData"
            :rows="rows"
            :rowHover="true"
            :loading="loading"
            :rowsPerPageOptions="[15, 30, 50, 100]"
            :globalFilterFields="['filterName']"
            paginator
            v-model:filters="filters"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
        >
            <template #header>
                <div class="flex justify-between card-header border-0 pt-6">
                    <div class="card-title">
                        <div class="my-1">
                            <span class="p-input-icon-left">
                                <i class="pi pi-search"/>
                                <InputText type="text"
                                    class="form-control w-[250px] ps-15"
                                    v-model="filters['global'].value"
                                    @input="trimInput()"
                                    placeholder="Rechercher un filtre" />
                            </span>
                        </div>
                    </div>
                    <div class="card-toolbar">
                        <div v-if="canAddProductFilterToCategory()">
                            <Button @click="onCreateFilter()" label="Ajouter un filtre" class="p-button-info" icon="pi pi-plus-circle" />
                        </div>
                        <div v-else>
                            <Button @click="onCreateFilter()" label="Ajouter un filtre" class="p-button-warning" icon="pi pi-info-circle" />
                        </div>
                    </div>
                </div>
            </template>
            <div class="card-body pt-0">
                <Column field="filterName" header="Filtre">
                    <template #body="slotProps">
                        <span> {{ slotProps.data.filterName }}</span>
                    </template>
                </Column>
                <Column field="filterType" header="Type de filtre">
                    <template #body="slotProps">
                        <span> {{ slotProps.data.filterType }}</span>
                    </template>
                </Column>
                <Column headerStyle="width: 12em;">
                    <template #header>
                        <div>Actions</div>
                    </template>
                    <template #body="slotProps">
                        <Button
                            size="small"
                            icon="pi pi-trash"
                            class="p-button-danger"
                            style="margin-right: 0.5em"
                            title="supprimer"
                            @click="onDeleteCategoryFilter(slotProps.data, $event)"
                        />
                        <InputSwitch
                            v-model="slotProps.data.isActive"
                            :input-id="slotProps.data.filterPropertyUuid"
                            @input="toggleStatus(
                                slotProps.data,
                                slotProps.data.isActive
                                ? `Êtes-vous sûr de vouloir activer ce filtre ?`
                                : `Êtes-vous sûr de vouloir désactiver ce filtre ?`,
                                slotProps.data.isActive
                                ? `Filtre activé, il sera visible sur la plateforme`
                                : `Filtre désactivé, il ne sera plus visible sur la plateforme`,
                                `Annuler`,
                                slotProps.data.isActive ? `Activer` : `Désctiver`,
                                slotProps.data.isActive
                                ? `btn btn-success`
                                : `btn btn-danger`,
                                `btn btn-secondary`
                            )"
                        />
                    </template>
                </Column>
            </div>
            <template #empty>Aucun filtre trouvé.</template>
        </DataTable>
    </div>
</template>

<script setup lang="ts">

const trimInput = () => {
    filters.value.global.value = filters.value.global?.value?.trim();
};

import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { categoryService } from "@/_services/category/category";
import { filterService } from "@/_services/filter/category/filter";
import { ICategory } from "@/models/category/ICategory";
import { ICategoryFilter } from "@/models/filter/category/ICategoryFilter";
import { FilterMatchMode } from "primevue/api";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputSwitch from "primevue/inputswitch";
import router from "@/router";
import swal from "@/_helpers/swal";

const loading = ref<boolean>(true);
const title = ref<string>("");
const route = useRoute();
const rows = ref<number>(15);
const categoryFilters: ICategoryFilter[] = [];
const tableData = ref<Array<ICategoryFilter>>(categoryFilters);
const categoryUuidRouteParam = ref<string | null>(null);
const category = ref<ICategory | null>(null);

const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'filterName': { value: null, matchMode: FilterMatchMode.CONTAINS },
});

onMounted(async () => {
    categoryUuidRouteParam.value = route.params.uuid as string;
    if (categoryUuidRouteParam.value) {
        category.value = await categoryService.fetchCategory(categoryUuidRouteParam.value);
        if (category.value) {
            title.value = category.value.name.toUpperCase();
            await populateDatatable(category.value.uuid);
        }
    }
});

const populateDatatable = async (categoryUuid: string) => {
    showSpinner();
    const [loadingDt, tableDataDt] = await filterService.getFiltersNamesLinkedToCategory(categoryUuid);
    loading.value = loadingDt.value;
    tableData.value = tableDataDt.value;
};

const refreshDatatable = () => {
    if (category.value) {
        populateDatatable(category.value.uuid);
    }
};

const refreshPage = () => {
    window.location.reload();
};

const redirectToLastPage = () => {
    router.back();
};

const onCreateFilter = () => {
    if (!canAddProductFilterToCategory()) {
        swal.warning("Cette catégorie n'est associé à aucun produit pour l'instant, Veuillez l'associer à un produit avant de l'associer à des filtres !")
    } else {
        if (category.value) {
            router.push({ name: 'addCategoryFilter' , params: { uuid: category.value.uuid }});
        }
    }
}

function canAddProductFilterToCategory(): boolean {
    if (category.value) {
        return categoryService.canAddProductFilterToCategory(category.value);
    }
    return false;
}

const toggleStatus = (
    categoryFilter: ICategoryFilter,
    confirmMessage: string,
    successMessage: string,
    cancelButtonText?: string,
    confirmButtonText?: string,
    confirmButtonClass?: string,
    cancelButtonClass?: string,
) => {
    swal
    .confirm(confirmMessage, cancelButtonText, confirmButtonText, confirmButtonClass, cancelButtonClass)
    .then((result) => {
        if (result.isConfirmed) {
            showSpinner();
            filterService.toggleFilterStatus(categoryFilter.filterUuid, categoryFilter.isActive).then(() => {
                let state = categoryFilter.isActive ? "Activé !" : "Désactivé !"
                swal.success(state, successMessage).then((response) => {
                    if (response.isConfirmed) {
                        hideSpinner();
                    }
                });
            });
        } else {
            refreshDatatable();
        }
    });
};

const onDeleteCategoryFilter = (categoryFilter: ICategoryFilter, event) => {
    swal
        .confirm("Êtes-vous sûr de vouloir supprimer ce filtre ?", "Non", "Oui")
        .then((responseLevel1: object) => {
            if (responseLevel1.isConfirmed) {
                const filterHTMLLine = event.target.closest('tr');
                showSpinner();
                filterService.deleteCategoryFilter(categoryFilter.filterUuid).then(() => {
                    hideSpinner();
                    swal.success("Supprimé !", "Le filtre a été supprimé").then((responseLevel2: object) => {
                        if (responseLevel2.isConfirmed) {
                            refreshDatatable();
                        }
                    });
                });
            }
        });
};

const showSpinner = () => {
    loading.value = true;
}

const hideSpinner = () => {
    loading.value = false;
}
</script>
